import { FinancialDocumentListItem } from "@common/types/financialDocument";
import {
    FinancialDocumentSortValue,
    FinancialDocumentView as FinancialDocumentViewType,
} from "@common/types/views/financialDocumentViews";
import { View } from "@common/types/views/views";
import type { ViewTableRowDragOut } from "@design-system/ViewTable/ViewTable";
import {
    type LoadMoreTableState,
    useViewTableLoadMore,
} from "@hooks/useLoadMoreTableState";
import React, { useMemo } from "react";
import { FinancialDocumentsView } from "./FinancialDocumentsView";
import {
    FinancialDocumentsViewFooter,
    type FinancialDocumentsViewFooterPagination,
} from "./FinancialDocumentsViewFooter";

interface FinancialDocumentsViewContainerProps {
    view: FinancialDocumentViewType;
    documents: FinancialDocumentListItem[] | null;
    total: number;
    /** Numbered pages; leave it out when the table loads more on scroll. */
    pagination?: FinancialDocumentsViewFooterPagination;
    onDocumentClick: (document?: FinancialDocumentListItem) => void;
    sort?: FinancialDocumentSortValue;
    onSortChange?: (sort: FinancialDocumentSortValue) => void;
    emptyView?: () => React.ReactNode;
    onViewChange?: (view: View) => void;
    selectionActions?: React.ReactNode;
    rowDragOut?: ViewTableRowDragOut<FinancialDocumentListItem>;
    loadMore?: LoadMoreTableState;
}

export const FinancialDocumentsViewContainer: React.FC<
    FinancialDocumentsViewContainerProps
> = ({
    view: externalView,
    documents,
    total,
    pagination,
    onDocumentClick,
    sort,
    onSortChange,
    emptyView,
    onViewChange,
    selectionActions,
    rowDragOut,
    loadMore,
}) => {
    const tableLoadMore = useViewTableLoadMore(
        loadMore,
        "Failed to load more documents",
    );
    const hasUnloadedRows = loadMore
        ? loadMore.hasMore
        : total > (documents?.length ?? 0);

    const footer = useMemo(
        () => (
            <FinancialDocumentsViewFooter
                total={total}
                hasUnloadedRows={hasUnloadedRows}
                pagination={pagination}
                selectionActions={selectionActions}
            />
        ),
        [total, hasUnloadedRows, pagination, selectionActions],
    );

    return (
        <FinancialDocumentsView
            view={externalView}
            documents={documents}
            onDocumentClick={onDocumentClick}
            sort={sort}
            onSortChange={onSortChange}
            emptyView={emptyView}
            onViewChange={onViewChange}
            footer={footer}
            rowDragOut={rowDragOut}
            loadMore={tableLoadMore}
        />
    );
};
